import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useSearchParams } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import {
  MyDocumentGroupsDocument,
  MyDocumentsDocument,
} from "@/graphql/generated/operations";
import { ServiceCard } from "@/pages/save/service/ServiceCard";
import {
  SERVICE_INPUTS,
  SERVICE_OUTPUTS,
  type ServiceCardSpec,
} from "@/pages/save/service/service-actions";
import { useServiceActions } from "@/pages/save/service/use-service-actions";

// Server page size for the document walk below, as on the Documents page.
const PAGE_FETCH = 100;

// Profile-header launcher for the Documents Input/Output actions. Renders the
// real ServiceCards in a popover and opens each flow's dialog IN PLACE on the
// profile (no navigation) via the shared useServiceActions hook.
export function ServiceMenu() {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  // Seed "open" from the deep link (?service=open) at first render, so arriving
  // from the Settings link shows the cards without ever calling setState in an
  // effect just to open. The param is stripped below.
  const [open, setOpen] = useState(() => searchParams.get("service") === "open");
  const [actionError, setActionError] = useState<string | null>(null);
  // Latched, not tied to `open`: opening a card CLOSES the popover and mounts
  // the dialog, which still needs the documents.
  const [docsNeeded, setDocsNeeded] = useState(() => searchParams.get("service") === "open");
  const wrapperRef = useRef<HTMLDivElement>(null);

  // The extraction dialog needs the user's documents to pick from; the profile
  // doesn't otherwise load them. ALL of them: a document past the first page
  // was simply absent from the analysis, with nothing on screen saying so.
  // Deferred until someone reaches for the menu — every node carries its card
  // thumbnail, which no reader of the profile page needs.
  const {
    data: docData,
    loading: docsFetching,
    refetch: docsRefetch,
    fetchMore: docsFetchMore,
  } = useQuery(MyDocumentsDocument, {
    variables: { input: { first: PAGE_FETCH } },
    skip: !docsNeeded,
  });
  // Each appended page (merged by the cursor field policy in graphql/client.ts)
  // advances pageInfo, re-running this until no page is left.
  const docsPageInfo = docData?.documents?.pageInfo;
  useEffect(() => {
    if (docsPageInfo?.hasNextPage && docsPageInfo.endCursor) {
      // A rejected page leaves the list incomplete, which `docsLoading` below
      // keeps saying; what it must not do is surface as an unhandled rejection.
      void docsFetchMore({
        variables: { input: { first: PAGE_FETCH, after: docsPageInfo.endCursor } },
      }).catch(() => {});
    }
  }, [docsPageInfo, docsFetchMore]);
  const { data: groupsData } = useQuery(MyDocumentGroupsDocument, { variables: { input: {} } });
  const docs = useMemo(
    () =>
      (docData?.documents?.nodes ?? []).map((d) => ({
        id: d.id,
        name: d.name ?? "",
        groupId: (d.group?.id as string | null) ?? null,
        createdAt: (d.createdAt as string | null) ?? null,
      })),
    [docData],
  );
  // DocumentGroupOutput is a single-member union - unwrap via the any seam.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const groupsOut: any = groupsData?.documentGroups;
  const groups = (groupsOut?.documentGroups ?? []) as readonly { id: string; name: string }[];

  const { runServiceAction, busyActions, dialogs } = useServiceActions({
    docs,
    groups,
    // Until every page is in, an absent document means "not yet", not "gone".
    docsLoading: docsNeeded && (docsFetching || !docData || !!docsPageInfo?.hasNextPage),
    // Only refresh a stock that was actually loaded: the ?service=linkedin deep
    // link runs a dialog without ever asking for the documents.
    onChanged: () => {
      if (docsNeeded) void docsRefetch();
    },
    onError: setActionError,
  });

  // Deep links: ?service=open (Settings "data transfer" link) seeded `open` above;
  // ?service=linkedin (tour follow-up) starts the LinkedIn import directly — it only opens a
  // dialog, so auto-running is safe. Everything else only ever OPENS the menu (an auto-run
  // export would download unprompted). Stripping the param right away keeps this idempotent
  // (the re-run sees no param) AND re-armed: the component stays mounted on the profile page,
  // and a later navigation that sets the param again (clicking the tour chip a second time)
  // must fire again — no once-per-mount latch.
  useEffect(() => {
    const value = searchParams.get("service");
    if (value !== "open" && value !== "linkedin") return;
    const next = new URLSearchParams(searchParams);
    next.delete("service");
    setSearchParams(next, { replace: true });
    if (value === "linkedin") runServiceAction("linkedin");
  }, [searchParams, setSearchParams, runServiceAction]);

  // Click/tap outside closes (mouse users also get hover-leave below). No
  // viewport scrim — it would sit above the button and flicker the hover loop.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const renderCard = (spec: ServiceCardSpec, accent: boolean) => {
    const busy = spec.action ? busyActions.has(spec.action) : false;
    return (
      <ServiceCard
        key={spec.titleId}
        icon={spec.icon}
        title={intl.formatMessage({ id: spec.titleId })}
        desc={intl.formatMessage({ id: spec.descId })}
        tags={
          busy
            ? intl.formatMessage({ id: "service.page.exporting", defaultMessage: "Exporting …" })
            : spec.tags
        }
        accent={accent}
        comingSoon={spec.comingSoon}
        disabled={busy || undefined}
        onClick={
          spec.action
            ? () => {
                setOpen(false);
                runServiceAction(spec.action!);
              }
            : undefined
        }
      />
    );
  };

  const groupLabel = (id: string) => (
    <div className="field-label mb-2">
      <FormattedMessage id={id} />
    </div>
  );

  return (
    <>
      {/* Hover zone = button + popover only. The dialogs render OUTSIDE it:
          a dialog is a full-screen modal, and if it were a descendant here,
          hovering it would re-fire onMouseEnter and re-open the popover behind
          the modal (close/re-open flicker). */}
      <div
        ref={wrapperRef}
        className="relative inline-block"
        onMouseEnter={() => {
          setDocsNeeded(true);
          setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
      >
        <Button
          variant="primary"
          size="compact"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => {
            setDocsNeeded(true);
            setOpen((v) => !v);
          }}
        >
          <FormattedMessage id="profile.page.serviceMenu" defaultMessage="Service" />
        </Button>
        {open ? (
          // No top gap so the pointer never crosses dead space (which would
          // flicker the menu). Cards mirror the Documents page Input/Output zone.
          <div className="toggle-pop absolute right-0 top-full z-30 max-h-[80vh] w-[min(92vw,420px)] origin-top-right overflow-auto rounded-md bg-surface p-4 shadow-lg ring-1 ring-ink/10">
            {groupLabel("service.page.inputTitle")}
            <div className="grid grid-cols-2 gap-3">
              {SERVICE_INPUTS.map((s) => renderCard(s, false))}
            </div>
            <div className="mt-4">{groupLabel("service.page.outputTitle")}</div>
            <div className="grid grid-cols-2 gap-3">
              {SERVICE_OUTPUTS.map((s) => renderCard(s, true))}
            </div>
            {actionError ? (
              <div className="field-meta mt-3 text-danger">{actionError}</div>
            ) : null}
          </div>
        ) : null}
      </div>
      {dialogs}
    </>
  );
}
